import {
    AppearanceFilterOption,
    CategoryFilterOption,
    SizeFilterOption,
} from '~/components/product-list/FilterOptions'
import {FilterOptions} from '~/gql/graphql'
import {useTranslations} from 'use-intl'

export type ProductFilters = Partial<
    Record<'categoryId' | 'departmentId' | 'appearanceId' | 'sizeId', string>
>

type FilterProps = {
    filter?: FilterOptions
    activeFilter?: ProductFilters
    onSelect: (filter: ProductFilters) => void
}

const Filter = ({filter, activeFilter, onSelect}: FilterProps) => {
    const t = useTranslations('productList.filter')
    return (
        <fieldset aria-label={t('title')} className="space-y-4 overflow-y-auto">
            {!!filter?.categories?.length && (
                <div>
                    <h3 className="pl-4 text-xs uppercase font-bold mb-2 text-gray-300">
                        {t('category')}
                    </h3>
                    <div className="space-y-1">
                        {filter.categories.map((department) => {
                            const isActiveDepartment =
                                !!department?.id && department.id === activeFilter?.departmentId
                            const hasActiveCategory = !!activeFilter?.categoryId

                            if (!department) {
                                return null
                            }

                            return (
                                <div key={department?.id}>
                                    <CategoryFilterOption
                                        category={department}
                                        isActive={isActiveDepartment}
                                        isSubFilterActive={hasActiveCategory}
                                        onActivate={() => {
                                            onSelect({departmentId: department?.id ?? undefined})
                                        }}
                                        onDeactivate={() => {
                                            onSelect({})
                                        }}
                                    >
                                        {department?.name}
                                    </CategoryFilterOption>
                                    {department?.subCategories?.length && (
                                        <div className="mt-1 mb-2 space-y-1">
                                            {department.subCategories.map((category) => {
                                                const isActiveCategory =
                                                    !!category?.id &&
                                                    category.id === activeFilter?.categoryId

                                                if (!category) {
                                                    return null
                                                }

                                                return (
                                                    <CategoryFilterOption
                                                        key={category.id}
                                                        category={category}
                                                        isSubFilter
                                                        isActive={isActiveCategory}
                                                        onActivate={() => {
                                                            onSelect({
                                                                ...activeFilter,
                                                                categoryId:
                                                                    category.id ?? undefined,
                                                            })
                                                        }}
                                                        onDeactivate={() => {
                                                            onSelect({
                                                                ...activeFilter,
                                                                categoryId: undefined,
                                                            })
                                                        }}
                                                    >
                                                        {category?.name}
                                                    </CategoryFilterOption>
                                                )
                                            })}
                                        </div>
                                    )}
                                </div>
                            )
                        })}
                    </div>
                </div>
            )}
            {!!filter?.appearances?.length && (
                <div>
                    <h3 className="pl-4 text-xs uppercase font-bold mb-2 text-gray-300">
                        {t('color')}
                    </h3>
                    <div className="flex flex-row flex-wrap gap-3 px-5 py-px">
                        {filter.appearances?.map((appearance) => {
                            const isActiveAppearance =
                                !!appearance?.id && appearance.id === activeFilter?.appearanceId

                            if (!appearance) {
                                return null
                            }

                            return (
                                <AppearanceFilterOption
                                    key={appearance?.id}
                                    appearance={appearance}
                                    isActive={isActiveAppearance}
                                    onActivate={() => {
                                        onSelect({
                                            ...activeFilter,
                                            appearanceId: appearance?.id ?? undefined,
                                        })
                                    }}
                                    onDeactivate={() =>
                                        onSelect({
                                            ...activeFilter,
                                            appearanceId: undefined,
                                        })
                                    }
                                />
                            )
                        })}
                    </div>
                </div>
            )}
            {!!filter?.sizes?.length && (
                <div>
                    <h3 className="pl-4 text-xs uppercase font-bold mb-2 text-gray-300">
                        {t('size')}
                    </h3>
                    <div className="flex flex-row flex-wrap gap-3 px-5 py-px">
                        {filter.sizes?.map((size) => {
                            const isActiveSize = !!size?.id && size.id === activeFilter?.sizeId

                            if (!size) {
                                return null
                            }

                            return (
                                <SizeFilterOption
                                    key={size?.id}
                                    size={size}
                                    isActive={isActiveSize}
                                    onActivate={() => {
                                        onSelect({
                                            ...activeFilter,
                                            sizeId: size?.id ?? undefined,
                                        })
                                    }}
                                    onDeactivate={() => {
                                        onSelect({
                                            ...activeFilter,
                                            sizeId: undefined,
                                        })
                                    }}
                                />
                            )
                        })}
                    </div>
                </div>
            )}
        </fieldset>
    )
}

export default Filter
